import { useEffect, useRef } from "react";
import { LineSeriesOption } from "echarts";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import { getInterval, getMaxValue, schoolMonths, tooltipMark } from "../reportUtil";
import { DraftType } from "./DraftType";
import { ProgressChartFilter } from "./ProgressChartFilter";

export interface ProgressChartData {
	year: number;
	draftType: DraftType;
	typeName: string;
	scores: (number | undefined)[];
}

export function MonthlyScoresChart(props: {
	className: string;
	filter: ProgressChartFilter;
	chartData: ProgressChartData[];
}) {
	const chartRef = useRef<ReactEcharts>(null);
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	const initialEChartOptions: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		legend: {
			show: true,
			padding: 15,
			itemGap: 20,
			textStyle: {
				color: foregroundColor,
			},
		},
		grid: {
			containLabel: true,
			right: 40,
			left: 40,
			top: 60,
			bottom: 25,
		},
		xAxis: {
			type: "category",
			data: schoolMonths,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		yAxis: {
			type: "value",
			min: 0,
			max: 30,
			interval: 5,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: "Average Score",
			nameLocation: "middle",
			nameGap: 40,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: {
					axisValue: string;
					dataIndex: number;
					color: string;
					seriesName: string;
					value: string;
				}[]
			) => {
				if (!params.find((p) => p.value)) return;

				const label = `<div>${schoolMonths[params[0].dataIndex].name}</div>`;
				const lineItem = (p: any) => {
					return p.value
						? `<div>${tooltipMark(p.color)} ${p.seriesName}: <b>${p.value}</b></div>`
						: "";
				};
				const content = params.reduce((a: string, v: any) => a + lineItem(v), label);

				return content;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: [],
	};

	useEffect(() => {
		const options = updateOptions(
			initialEChartOptions,
			getInterval(props.filter.trait),
			getMaxValue(props.filter.trait),
			getSeriesData(props.chartData, foregroundColor)
		);

		if (chartPalette != undefined) {
			options.color = chartPalette;
		}

		chartRef.current?.getEchartsInstance().setOption(options, true);
	}, [props.filter.trait, props.chartData, chartPalette, foregroundColor, accentColor]);

	return (
		<>
			{props.chartData.length > 0 ? (
				<ReactEcharts
					ref={chartRef}
					option={initialEChartOptions}
					className="w-full mt-4 !h-96"
				/>
			) : (
				<div className="w-full mt-4 h-96 border-2 border-gray-200 flex items-center justify-center bg-gray-100">
					<p>No scores to display</p>
				</div>
			)}
		</>
	);
}

const getSeriesData = (chartData: ProgressChartData[], foregroundColor: string) =>
	chartData.map((data): LineSeriesOption => buildSeriesProps(data, foregroundColor));

function buildSeriesProps(data: ProgressChartData, foregroundColor: string): LineSeriesOption {
	return {
		type: "line",
		name: `${data.year}–${data.year + 1} — ${data.typeName}`,
		connectNulls: true,
		data: data.scores,
		label: {
			show: true,
			color: foregroundColor,
		},
		lineStyle: {
			type: data.draftType === DraftType.Last ? "solid" : "dashed",
		},
		emphasis: {
			focus: "series",
		},
	};
}

function updateOptions(
	options: EChartsOption,
	yAxisInterval: number,
	yAxisMax: number,
	seriesData: LineSeriesOption[]
): EChartsOption {
	options.yAxis.interval = yAxisInterval;
	options.yAxis.max = yAxisMax;
	options.series = seriesData;
	return options;
}
